import { EngagementV1DTO, ValidationError } from '@beable/roster-backend-client-v1';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import AppBar from '@mui/material/AppBar';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Toolbar from '@mui/material/Toolbar';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../Header/Header';
import { BasePaper } from '../../Common/Layout/BasePaper';
import { styled } from '@mui/material/styles';
import { FormikErrors, useFormik } from 'formik';
import RosterBackendService from '../../../services/RosterBackendService';
import { EditableEngagement, EngagementScheduleWithSiteVisibility, ToastNotification, allUserExperiences, emptyEngagement, emptySchedule, findSetLocalization, getEngagementIcon, iconMap, toEditableEngagement } from './types';
import EditSchedule from './EditSchedule';
import AddIcon from '@mui/icons-material/Add';
import useEngagements from './useEngagements';
import EngagementList from './EngagementList';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import { useNavigate, useParams } from 'react-router';
import ManageEngagementToolbar from './ManageEngagementToolbar';
import { values } from 'lodash';
import { APIErrorResponse } from '@beable/mwm-commons-endpoint-client';

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 800
}));

const Section = styled(Box)(() => ({
  border: '1px solid #50248D',
  margin: '10px 0px'
}));

const UserExperiencesLabel = styled(Typography)(() => ({
  alignSelf: 'center',
  verticalAlign: 'middle'
}));

const SubHeader = styled(Box)(({ theme }) => ({
  color: theme.palette.background.paper,
  backgroundColor: theme.palette.primary.main,
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
}));

const UrlTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    fontFamily: 'monospace',
    fontSize: '14px'
  },
});

const PublishedSwitch = styled(FormControlLabel)(() => ({
  minWidth: '180px'
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    fontSize: '14px'
  },
});

const StyledTableCell = styled(TableCell)({
  verticalAlign: 'top',
  padding: '5px'
});

const StyledPaper = styled(BasePaper)({
  margin: '20px'
});

export default function ManageEngagements() {
  const navigate = useNavigate();
  const { engagements, refreshEngagementList } = useEngagements();
  const { engagementId: urlEngagementId } = useParams<{ engagementId?: string | undefined; }>();
  const [selectedEngagement, setSelectedEngagement] = useState<EditableEngagement | null>(null);
  const [showEngagementsDrawer, setShowEngagementsDrawer] = useState(urlEngagementId ? false : true);
  const [showUnpublished, setShowUnpublished] = useState(true);
  const [notification, setNotification] = useState<ToastNotification | null>(null);

  const defaultEngagement = useMemo(() => emptyEngagement(), []);

  /**
   * Converts class-validator Constraint errors to FormikErrors
   */
  const errorsToFormikErrors = (errors: ValidationError[]) => {
    const result: any = {};
    for (const error of errors) {
      const property = error.property;
      result[property] = values(error.constraints)[0];
      if (error.children?.length) {
        result[property] = errorsToFormikErrors(error.children);
      }
    }
    return result;
  };

  const getErrorMessage = (formikErrors: any) => {
    let message = 'Error Saving Engagement';
    const propsToInlineMessage = ['active'];
    propsToInlineMessage.forEach(prop => {
      if(prop in formikErrors) {
        message = `${message}: ${formikErrors[prop]}`
      }
    })
    return message;
  };

  const formik = useFormik<EditableEngagement>({
    initialValues: selectedEngagement || defaultEngagement,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const result = await RosterBackendService.saveEngagement(values);
      if (result.isErr()) {
        console.error(result.error);
        const errorResult = result.error.result.unwrapOr({}) as Partial<APIErrorResponse<string>>;
        const validationErrors = Array.isArray(errorResult.message) ? errorResult.message : [];
        const formikErrors = errorsToFormikErrors(validationErrors);
        setNotification({
          error: true,
          message: getErrorMessage(formikErrors),
        });
        formik.setErrors(formikErrors);
        return;
      }
      setNotification({
        error: false,
        message: `Engagement Saved`
      });
      // Once saved we need to update formik to let it know about the updated record and that the form is no longer dirty
      formik.resetForm();
      
      setSelectedEngagement(
        toEditableEngagement(result.value)
      );
      await refreshEngagementList();
    }
  });

  /**
   * If an engagementId is passed URL params look for that id in the engagements list 
   * and select set it as the selected engagement.
   */
  useEffect(() => {
    if (urlEngagementId && engagements.length) {
      const engagementFromUrl = engagements.find(e => e.id === urlEngagementId);
      if (!engagementFromUrl) {
        return;
      }
      handleEngagementSelected(engagementFromUrl);
      setShowEngagementsDrawer(false);
    }
  }, [urlEngagementId, engagements]);
  
  const handleEngagementSelected = (engagement?: EngagementV1DTO) => {
    if(!engagement) { 
      return setSelectedEngagement(null);
    }
    setSelectedEngagement(
      toEditableEngagement(engagement)
    );
    navigate(`/manage-engagements/${engagement.id}`);
    setShowEngagementsDrawer(false);
  };

  const handleOnEdit = (engagement: EngagementV1DTO) => {
    handleEngagementSelected(engagement);
  };


  const handleNewEngagement = () => {
    const newEngagement = emptyEngagement();
    handleEngagementSelected(newEngagement);
  };

  const handleNewSchedule = () => {
    if (!selectedEngagement) { return; }
    formik.setFieldValue(
      'schedules',
      [
        ...formik.values.schedules,
        emptySchedule()
      ]
    );
    formik.setFieldTouched('schedules');
  };

  const title = findSetLocalization(formik.values)?.title || 'New Engagement';

  return (
    <div>
      <Header />
      <ManageEngagementToolbar
        onNewEngagementClick={handleNewEngagement}
        onShowEngagementsClick={() => setShowEngagementsDrawer(true)}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={notification !== null} autoHideDuration={6000}
        onClose={() => setNotification(null)}
      >
        <Alert severity={notification?.error ? 'error' : 'success'}>{notification?.message}</Alert>
      </Snackbar>
      <Drawer
        open={showEngagementsDrawer}
        onClose={() => setShowEngagementsDrawer(false)}
      >
        <Toolbar />
        {engagements.length ? <>
          <Box
            sx={{
              display: 'flex',
              columnGap: 1,
              justifyContent: 'end',
              bgcolor: '#f1e7ff',
              border: '1px solid #cebde4'
            }}>
            <PublishedSwitch control={<Switch
              color="secondary"
              checked={!showUnpublished}
              onChange={() => {
                setShowUnpublished(!showUnpublished)
              }} />} label={`${showUnpublished ? 'Showing All' : 'Only Published'}`} />
          </Box>
          <EngagementList
            engagements={engagements}
            onSelect={handleOnEdit}
            selected={selectedEngagement}
            showUnpublished={showUnpublished}
          /></>
          :
          <Alert
            variant="outlined"
            severity="warning"
            sx={{ margin: '10px' }}
          >
            No Engagements Found
            <Button
                        variant="contained"
                        color="secondary"
                        fullWidth={true}
                        data-cy={`new-engagement-inline-button`}
                        data-testid={`new-engagement-inline-button`}
                        onClick={handleNewEngagement}
                      >
                        create an engagement
                      </Button>
          </Alert>
        }
      </Drawer>
      <StyledPaper>

        {!selectedEngagement && <Alert
          variant="outlined"
          severity="warning"
        >
          Please select an Engagement or create a new one
        </Alert>
        }

        {selectedEngagement &&
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid size={12}>
                <SubHeader>
                  <Typography variant="h5" align-left='true'>
                    Engagement: {title}
                  </Typography>
                  <Tooltip 
                    title={`Published Engagements will appear on Sites with corresponding Schedules.${formik.dirty ? ' Engagement has unsaved changes!' : '' }`}
                  >
                  <PublishedSwitch control={<Switch
                    color="secondary"
                    checked={formik.values.active}
                    icon={<UnpublishedIcon />}
                    name='active'
                    onChange={formik.handleChange} />} 
                    onBlur={formik.handleBlur}
                    label={`${formik.values.active ? 'Published' : 'Unpublished'} ${formik.dirty ? '*' : ''}`} 
                  />
                  </Tooltip>
                </SubHeader>

                <Section>
                  <SubHeader>
                    <Typography variant='h6' component='span'>Banner Design</Typography>
                  </SubHeader>
                  <TableContainer sx={{ padding: '10px' }} >
                    <Table aria-label="manage engagement localization strings">
                      <TableHead>
                        <TableRow >
                          <TableHeaderCell ></TableHeaderCell>
                          <TableHeaderCell >Title</TableHeaderCell>
                          <TableHeaderCell >Subtext</TableHeaderCell>
                          <TableHeaderCell >Button Text</TableHeaderCell>
                          <TableHeaderCell >Button Link</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ paddingTop: '10px' }}>
                        {formik.values.localizations.map((localization, index) => (

                          <TableRow key={localization.id}>
                            <TableHeaderCell component="td" scope="row" >
                              {localization.language.toUpperCase()}
                            </TableHeaderCell>
                            <StyledTableCell component="td" scope="row" sx={{ verticalAlign: 'top' }}>
                              <StyledTextField
                                variant="outlined"
                                fullWidth={true}
                                size='small'
                                value={formik.values.localizations[index].title}
                                slotProps={{
                                  htmlInput: {
                                    'data-cy': `localization-${localization.language}-title`,
                                    'data-testid': `localization-${localization.language}-title`
                                  }
                                }}
                                onChange={formik.handleChange}
                                type="text"
                                multiline={true}
                                name={`localizations.${index}.title`}
                                helperText={"Header Text on Engagement"}
                              />
                            </StyledTableCell>
                            <StyledTableCell component="td" scope="row" >
                              <StyledTextField
                                variant="outlined"
                                fullWidth={true}
                                size='small'
                                value={formik.values.localizations[index].subtext}
                                slotProps={{ 
                                  htmlInput: {
                                    'data-cy': `localization-${localization.language}-subtext`,
                                    'data-testid': `localization-${localization.language}-subtext`,
                                  }
                                }}
                                onChange={formik.handleChange}
                                type="text"
                                multiline={true}
                                name={`localizations.${index}.subtext`}
                                helperText={"Appears below Title"}
                              />
                            </StyledTableCell>
                            <StyledTableCell component="td" scope="row">
                              <StyledTextField
                                variant="outlined"
                                fullWidth={true}
                                size='small'
                                value={formik.values.localizations[index].buttonText}
                                slotProps={{ 
                                  htmlInput: {
                                    'data-cy': `localization-${localization.language}-buttonText`,
                                    'data-testid': `localization-${localization.language}-buttonText`
                                  }
                                }}
                                onChange={formik.handleChange}
                                type="text"
                                multiline={true}
                                name={`localizations.${index}.buttonText`}
                                helperText={"Text for Button"}
                              />
                            </StyledTableCell>
                            <StyledTableCell component="td" scope="row">
                              <UrlTextField
                                variant="outlined"
                                fullWidth={true}
                                size='small'
                                value={formik.values.localizations[index].externalLink}
                                slotProps={{ 
                                  htmlInput: {
                                    'data-cy': `localization-${localization.language}-externalLink`,
                                    'data-testid': `localization-${localization.language}-externalLink`
                                  }
                                }}
                                onChange={formik.handleChange}
                                type="text"
                                multiline={true}
                                name={`localizations.${index}.externalLink`}
                                helperText={"Link or javascript snippet"}
                              />
                            </StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box
                    sx={{
                      padding: 2,
                      display: 'flex'
                    }}>
                    <FormControl fullWidth>
                      <InputLabel id="select-icon-label">Icon</InputLabel>
                      <Select
                        labelId="select-icon-label"
                        id="select-icon-label"
                        value={formik.values.icon}
                        label="Icon"
                        name="icon"
                        startAdornment={
                          <InputAdornment position='start'>
                            {getEngagementIcon(formik.values.icon)}
                          </InputAdornment>
                        }
                        onChange={formik.handleChange}
                      >
                        {Object.keys(iconMap).map(iconName => (
                          <MenuItem key={iconName} value={iconName} data-cy={`engagement-icon-${iconName}`}>{iconMap[iconName].label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Section>

                <Section>
                  <AppBar position='static'>
                    <Toolbar>
                      <Typography variant='h6' component='span'>Schedule</Typography>

                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        data-cy={`new-schedule-button`}
                        data-testid={`new-schedule-button`}
                        onClick={handleNewSchedule}
                      >
                        New Schedule
                      </Button>
                    </Toolbar>
                  </AppBar>
                  <Box sx={{ padding: '10px' }}>
                  <FormControl
                    required
                    error={formik.errors.activeExperiences != ''}
                    component="div"
                    sx={{ display: 'flex', justifyContent: 'right' }}
                    variant="standard"
                  >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'right'
                        }}>
                      <UserExperiencesLabel>Show on User Experiences:</UserExperiencesLabel>
                      <FormGroup row={true}>
                        {allUserExperiences.map(userExperience => (
                          <FormControlLabel
                            key={userExperience}
                            
                            control={
                              <Checkbox
                                name="activeExperiences"
                                value={userExperience}
                                onChange={formik.handleChange}
                                checked={formik.values.activeExperiences.includes(userExperience)}
                              />
                            }
                            
                            label={<Typography>{userExperience}</Typography>}
                          />
                        ))}
                      </FormGroup>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'right'
                        }}>

                      <FormHelperText>{formik.errors.activeExperiences}</FormHelperText>
                      </Box>
                    </FormControl>

                    {formik.values.schedules.map((schedule, index) => { 
                      const scheduleErrors = formik.errors?.schedules ? formik.errors?.schedules[index] as FormikErrors<EngagementScheduleWithSiteVisibility> : undefined;
                      return (
                      <EditSchedule
                        key={schedule.id}
                        schedule={schedule}
                        formik={formik}
                        errors={scheduleErrors}
                        fieldValuePrefix={`schedules.${index}`}
                        setNotification={setNotification}
                      />
                      )
                    })}

                  </Box>
                </Section>
              </Grid>
            </Grid>

            <ButtonGroup color="primary" sx={{ display: 'flex', justifyContent: 'flex-end' }}  >
              <Button
                color='primary'
                variant='contained'
                data-cy="save-engagement-button"
                type="submit"
                disabled={formik.isSubmitting || !formik.dirty}
              >
                Save Engagement
              </Button>
              <Tooltip title="Undo all Changes">
                <span>
                  <Button
                    color='primary'
                    variant='text'
                    data-cy="cancel-save-engagement-button"
                    data-testid="cancel-save-engagement-button"
                    type="submit"
                    onClick={() => { formik.resetForm(); }}
                    disabled={formik.isSubmitting || !formik.dirty}
                    
                  >
                    Cancel
                  </Button>
                </span>
              </Tooltip>
            </ButtonGroup>
          </form>
        }

      </StyledPaper>
    </div>
  );
}